import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { PackagePlus, ArrowLeftRight, ClipboardCheck, Briefcase, Undo2, Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { SerialScanCapture } from "@/components/stock/SerialScanCapture";

const sel = "w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
const lbl = "text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1 block";

export function useLocations() {
  const [locations, setLocations] = useState([]);
  const reload = () => api.get("/stock/locations").then((r) => setLocations(r.data.items || [])).catch(() => {});
  useEffect(() => { reload(); }, []);
  return [locations, reload];
}

function Field({ label, children }) {
  return <div><span className={lbl}>{label}</span>{children}</div>;
}

function ActionModal({ open, onOpenChange, title, description, icon: Icon, onSubmit, busy, submitLabel, children, testid }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-indigo-500" />} {title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3 py-1">{children}</div>
        <DialogFooter className="sticky bottom-0 bg-background pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="stock-action-cancel">Cancel</Button>
          <Button onClick={onSubmit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="stock-action-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LocationSelect({ value, onChange, locations, testid, placeholder = "Select location", counts = null }) {
  const list = counts ? locations.filter((l) => (counts[l.location_id] || 0) > 0) : locations;
  return (
    <select className={sel} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">{counts && list.length === 0 ? "No stock at any location" : placeholder}</option>
      {list.map((l) => <option key={l.location_id} value={l.location_id}>{counts ? `${l.name} (${counts[l.location_id]})` : `${l.name} (${l.type})`}</option>)}
    </select>
  );
}

export function StockActionsBar({ productId, locations, byLocation = [], onDone, serialized, jobId, customerId }) {
  const srcCounts = Object.fromEntries((byLocation || []).map((l) => [l.location_id, l.qty_available ?? l.qty_held ?? 0]));
  const { user } = useAuth();
  const canWrite = user?.is_super || user?.permissions?.includes("stock.adjust") || user?.permissions?.includes("stock.manage");
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = () => setModal(null);
  const done = (msg) => { toast.success(msg); close(); onDone && onDone(); };
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");

  // form state
  const [loc, setLoc] = useState("");
  const [toLoc, setToLoc] = useState("");
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");
  const [supplier, setSupplier] = useState("");
  const [count, setCount] = useState("");
  const [job, setJob] = useState(jobId || "");
  const [jobOpts, setJobOpts] = useState([]);
  const [serials, setSerials] = useState([]);
  const searchJobs = (q) => { if (!q || q.length < 2) { setJobOpts([]); return; } api.get("/search", { params: { q } }).then((r) => setJobOpts((r.data.jobs || []).map((j) => ({ id: j.id, title: j.title })))).catch(() => {}); };
  const open = (m) => { setLoc(""); setToLoc(""); setQty(""); setNotes(""); setSupplier(""); setCount(""); setJob(jobId || ""); setSerials([]); setModal(m); };

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { err(e); } finally { setBusy(false); } };

  if (!canWrite) return null;

  return (
    <>
      <datalist id="job-search-options">{jobOpts.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}</datalist>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => open("transfer")} data-testid="stock-btn-transfer"><ArrowLeftRight className="w-4 h-4" /> Transfer</Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => open("adjust")} data-testid="stock-btn-adjust"><ClipboardCheck className="w-4 h-4" /> Stocktake / Adjust</Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => open("allocate")} data-testid="stock-btn-allocate"><Briefcase className="w-4 h-4" /> Allocate to Job</Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => open("return")} data-testid="stock-btn-return"><Undo2 className="w-4 h-4" /> Return</Button>
      </div>

      <ActionModal open={modal === "transfer"} onOpenChange={close} title="Transfer Stock" icon={ArrowLeftRight}
        busy={busy} submitLabel="Transfer" testid="modal-transfer"
        onSubmit={() => run(async () => {
          const to = locations.find((l) => l.location_id === toLoc);
          await api.post("/stock/transfer", { product_legacy_id: productId, from_location_id: loc, to_location_id: toLoc, quantity: Number(qty), van: to?.type === "van", notes });
          done("Stock transferred");
        })}>
        <Field label="From"><LocationSelect value={loc} onChange={setLoc} locations={locations} counts={srcCounts} testid="transfer-from" /></Field>
        <Field label="To"><LocationSelect value={toLoc} onChange={setToLoc} locations={locations} testid="transfer-to" /></Field>
        <Field label="Quantity"><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="transfer-qty" /></Field>
        <Field label="Notes (optional)"><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="transfer-notes" /></Field>
      </ActionModal>

      <ActionModal open={modal === "adjust"} onOpenChange={close} title="Stocktake / Adjustment" icon={ClipboardCheck}
        busy={busy} submitLabel="Save Count" testid="modal-adjust"
        description="Enter the physically counted quantity at the location. A reversing movement is recorded — history is never deleted."
        onSubmit={() => run(async () => {
          await api.post("/stock/adjust", { product_legacy_id: productId, location_id: loc, counted_quantity: Number(count), stocktake: true, notes });
          done("Stocktake recorded");
        })}>
        <Field label="Location"><LocationSelect value={loc} onChange={setLoc} locations={locations} testid="adjust-location" /></Field>
        <Field label="Counted Quantity"><Input type="number" value={count} onChange={(e) => setCount(e.target.value)} data-testid="adjust-count" /></Field>
        <Field label="Reason / Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="adjust-notes" /></Field>
      </ActionModal>

      <ActionModal open={modal === "allocate"} onOpenChange={close} title="Allocate to Job" icon={Briefcase}
        busy={busy} submitLabel="Allocate" testid="modal-allocate"
        onSubmit={() => run(async () => {
          if (serialized && (serials.length !== Number(qty) || !Number(qty))) { toast.error(`Scan exactly ${Number(qty) || 0} serial number(s)`); return; }
          await api.post("/stock/allocate", { product_legacy_id: productId, location_id: loc, quantity: Number(qty), job_legacy_id: job, customer_id: customerId, notes, serial_numbers: serialized ? serials : [] });
          done("Allocated to job");
        })}>
        <Field label="From Location"><LocationSelect value={loc} onChange={setLoc} locations={locations} counts={srcCounts} testid="allocate-location" /></Field>
        <Field label="Job ID"><Input value={job} onChange={(e) => { setJob(e.target.value); searchJobs(e.target.value); }} placeholder="Search job by name or ID…" list="job-search-options" data-testid="allocate-job" disabled={!!jobId} /></Field>
        <Field label="Quantity"><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="allocate-qty" /></Field>
        {serialized && Number(qty) > 0 && (
          <Field label={`Serial numbers (scan ${Number(qty)})`}>
            <SerialScanCapture value={serials} required={Number(qty)} onChange={setSerials} testid="allocate-serial-scan" />
          </Field>
        )}
        <Field label="Notes (optional)"><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="allocate-notes" /></Field>
      </ActionModal>

      <ActionModal open={modal === "return"} onOpenChange={close} title="Return Stock" icon={Undo2}
        busy={busy} submitLabel="Return to Stock" testid="modal-return"
        onSubmit={() => run(async () => {
          await api.post("/stock/return", { product_legacy_id: productId, location_id: loc, quantity: Number(qty), job_legacy_id: job || null, from_allocation: !!job, notes });
          done("Stock returned");
        })}>
        <Field label="To Location"><LocationSelect value={loc} onChange={setLoc} locations={locations} testid="return-location" /></Field>
        <Field label="Quantity"><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="return-qty" /></Field>
        <Field label="From Job (optional)"><Input value={job} onChange={(e) => { setJob(e.target.value); searchJobs(e.target.value); }} placeholder="Search job by name or ID…" list="job-search-options" data-testid="return-job" /></Field>
        <Field label="Notes (optional)"><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="return-notes" /></Field>
      </ActionModal>
    </>
  );
}

export { LocationSelect, ActionModal, Field, sel, lbl };
